import { Fragment, useState } from 'react';

import { Card, Col, Popover, Row, Space } from 'antd';
import { AchBankLinkConstant } from 'src/constants';
import { useApplicationConfigs } from 'src/hooks';
import { MModalV2, MButton, MDivider } from 'src/lib';
import { Color, Images, Spacing } from 'src/styles';

import { YourAchBankLinkNotApprovedAlertMessage } from '../YourAchBankLinkNotApprovedAlertMessage/YourAchBankLinkNotApprovedAlertMessage';
import { YourTransferNotCompletedAlertMessage } from '../YourTransferNotCompletedAlertMessage/YourTransferNotCompletedAlertmessage';

import { AchBankLinkStatusTag } from './AchBankLinkStatusTag';
import * as Styles from './BankAccountItem.styles';

export enum ACHRelationshipActionType {
  DEPOSIT = 'deposit',
  WITHDRAWAL = 'withdrawal',
  EDIT = 'edit',
  UNLINK = 'unlink',
}

export const achRelationshipsActions = [
  {
    label: 'Edit Nickname',
    img: 'ri-edit-line',
    type: ACHRelationshipActionType.EDIT,
  },
  {
    label: 'Unlink',
    img: 'ri-link-unlink',
    type: ACHRelationshipActionType.UNLINK,
  },
];

const BankAccountItem = ({ account, onClick, onDeposit, onWithdrawal, onEdit, onUnlink }: any) => {
  const [visible, setIsVisible] = useState(false);
  const [isDisabledDepositModalOpen, setIsDisabledDepositModalOpen] = useState(false);
  const [isDisabledWithdrawalModalOpen, setIsDisabledWithdrawalModalOpen] = useState(false);

  const { deposit, withdrawal } = useApplicationConfigs();

  const isBankLinkPending = () =>
    AchBankLinkConstant.PENDING_STATUS_LIST.includes(account?.tradingBlockACHRelationship.state);

  const handleVisibleChange = (visible: boolean) => {
    setIsVisible(visible);
  };

  const onCloseDisabledTransferModal = () => {
    if (isDisabledDepositModalOpen) {
      setIsDisabledDepositModalOpen(false);

      return;
    }
    setIsDisabledWithdrawalModalOpen(false);
  };

  const onDropDownClick = (action: { type: ACHRelationshipActionType }) => {
    if (action.type === ACHRelationshipActionType.EDIT) {
      setIsVisible(false);
      onEdit();
    } else if (action.type === ACHRelationshipActionType.UNLINK) {
      setIsVisible(false);
      onUnlink();
    }
  };

  const onDepositAction = () => {
    if (deposit.isDisabled) {
      setIsDisabledDepositModalOpen(true);

      return;
    }
    onClick();

    return onDeposit();
  };

  const onWithdrawalAction = () => {
    if (withdrawal.isDisabled) {
      setIsDisabledWithdrawalModalOpen(true);

      return;
    }
    onClick();
    onWithdrawal();
  };

  return (
    <Fragment>
      <Card className={Styles.cardWrapper}>
        <Row justify={'center'}>
          <Col>
            <Space direction='horizontal' align='start'>
              <div className={Styles.bankImageContainer}>
                <img
                  alt=''
                  src={
                    account?.tradingBlockPlaidInstitution?.logo
                      ? `data:image/jpeg;base64,${account?.tradingBlockPlaidInstitution?.logo}`
                      : Images.BankPlaceholder
                  }
                  className={Styles.bankImage}
                />
              </div>
              <div
                style={{
                  flex: 1,
                  justifyContent: 'center',
                }}>
                <>
                  <Row>
                    <div className={Styles.bankAccountTypeLabel}>
                      {account.tradingBlockACHRelationship?.bankAccountType}
                    </div>
                    {account?.tradingBlockACHRelationship?.nickName && (
                      <div className={Styles.nicknameLabel}>{`(${account.tradingBlockACHRelationship.nickName})`}</div>
                    )}
                  </Row>
                  <div className={Styles.bankAccountLabel}>
                    {account?.tradingBlockACHRelationship?.bankAccount?.replace(/^(.{3})(.{3})(.*)$/, '$1 $2 $3')}
                  </div>
                </>
              </div>
              <AchBankLinkStatusTag
                state={account?.tradingBlockACHRelationship.state}
                testId={'link-bank-account-status'}
              />
            </Space>
          </Col>
          <Col flex='auto'>
            <div className={Styles.endTextAlignment} onClick={onClick}>
              <Popover
                getPopupContainer={(trigger: any) => trigger.parentElement}
                content={
                  <div className={Styles.popoverContent}>
                    <div className={Styles.popoverTitle}>What would you like to do</div>
                    <div>
                      {achRelationshipsActions.map((action, index) => {
                        const colorFont =
                          action.type === ACHRelationshipActionType.EDIT
                            ? Color.GRAYSCALE.GRAY8
                            : action.type === ACHRelationshipActionType.UNLINK
                            ? Color.RED.RED6
                            : Color.BRAND.BRAND6;

                        return (
                          <div
                            key={`${action.label}_key`}
                            className={Styles.actionRowContainer}
                            style={{
                              borderBottom: index === achRelationshipsActions.length - 1 ? '0px' : '1px solid #F2F5F7',
                            }}>
                            <div
                              className={Styles.actionRow}
                              style={{
                                color: colorFont,
                              }}
                              onClick={() => onDropDownClick(action)}
                              data-testid={`link-bank-account-btn-${action.label.toLowerCase()}`}>
                              <i className={`${action.img} ${Styles.actionImage}`} style={{ color: colorFont }} />
                              {action.label}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                }
                trigger='click'
                open={visible}
                onOpenChange={handleVisibleChange}
                placement='bottomRight'>
                <i className={`${Styles.moreVerticalImage} ri-more-2-line`} />
              </Popover>
            </div>
          </Col>
          <MDivider />
          <Col span={24} className={Styles.endTextAlignment}>
            <YourTransferNotCompletedAlertMessage isAchBankLinkPending={isBankLinkPending()} />
            <YourAchBankLinkNotApprovedAlertMessage isAchBankLinkPending={isBankLinkPending()} />
            <Space direction='horizontal'>
              <MButton onClick={onDepositAction} type='secondary' testId={'money-transfers-btn-deposit'}>
                Deposit Funds
              </MButton>
              <MButton onClick={onWithdrawalAction} type='secondary' testId={'money-transfers-btn-withdrawal'}>
                Withdrawal Funds
              </MButton>
            </Space>
          </Col>
        </Row>
      </Card>

      <MModalV2
        title='Bank Account Transfering Information'
        open={isDisabledDepositModalOpen || isDisabledWithdrawalModalOpen}
        onClose={onCloseDisabledTransferModal}
        body={isDisabledDepositModalOpen ? deposit.disclaimer : withdrawal.disclaimer}
        footer={
          <MButton type='tertiary' onClick={onCloseDisabledTransferModal} className={Spacing.my4}>
            Close
          </MButton>
        }
      />
    </Fragment>
  );
};

export default BankAccountItem;
